import classNames from "classnames";
import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminWorkflowsApi } from "../../http/workflow/api";
import { getIconProps, Workflow, WorkflowLog } from "../../http/workflow/types";
import { useWorkflowAttachmentsApi } from "../../http/workflowAttachments/api";
import { formatTimeStampNumeric } from "../../types/utils";
import SvgAttachFile from "../icons/AttachFile";
import SvgInfo from "../icons/Info";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import Inline from "../ui/Inline";
import ListFiles from "../ui/ListFiles";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import SubmitBar from "../ui/SubmitBar";
import Table from "../ui/Table";
import Textarea from "../ui/Textarea";
import styles from "./CheckWorkflowDialog.module.css";
import RejectForm, { RejectFormBody } from "./RejectForm";

type CheckWorkflowDialogProps = {
  workflowId: string;
  onClose: () => void;
  carId: string;
};

const CheckWorkflowDialog: FC<CheckWorkflowDialogProps> = ({
  workflowId,
  onClose,
  carId,
}: CheckWorkflowDialogProps) => {
  const i18n = useI18n();
  const workflowApi = useAdminWorkflowsApi();
  const workflowAttachmentsApi = useWorkflowAttachmentsApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [showAcceptDialog, setShowAcceptDialog] = useState<boolean>(false);
  const [showRejectDialog, setShowRejectDialog] = useState<boolean>(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState<Workflow>();
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>();
  const [showReopenDialog, setShowReopenDialog] = useState<boolean>();
  const [showImagesDialog, setShowImagesDialog] = useState<boolean>();
  const [showTextDialog, setShowTextDialog] = useState<string>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const resultWorkflow = workflowApi.getWorkflow(workflowId, aborter.current.signal);

  const [dataWorkflow] = useDataInitializer(resultWorkflow.data, resultWorkflow.error);

  const resultAttachments = workflowAttachmentsApi.useWorkflowAttachments(aborter.current.signal, workflowId);
  const [dataAttachment] = useDataInitializer(resultAttachments.data, resultAttachments.error);

  useEffect(() => {
    resultAttachments.error && dispatch(showErrorNotification(i18n, resultAttachments.error));
  }, [resultAttachments.error]);

  const rejectImages = async (formData: RejectFormBody) => {
    setIsSubmitting(true);
    try {
      await workflowApi.rejectImages(workflowId, formData.text, carId);
      dispatch(showSuccessNotification(i18n.t));
      setShowRejectDialog(false);
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
    setIsSubmitting(false);
  };

  const approveImages = async () => {
    setIsSubmitting(true);
    try {
      await workflowApi.approveImages(workflowId, carId);
      dispatch(showSuccessNotification(i18n.t));
      setShowRejectDialog(false);
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
    setIsSubmitting(false);
  };

  const notifyDriver = async (item: Workflow) => {
    setIsSubmitting(true);
    try {
      await workflowApi.notifyDriver(item.id, carId!);
      dispatch(showSuccessNotification(i18n.t));
      setShowNotifyDialog(undefined);
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
    setIsSubmitting(false);
  };

  const cancelWorkflow = async (item: Workflow) => {
    setIsSubmitting(true);
    try {
      await workflowApi.cancelWorkflow(item.id, carId!);
      dispatch(showSuccessNotification(i18n.t));
      setShowCancelDialog(false);
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
    setIsSubmitting(false);
  };

  const reopenWorkflow = async (item: Workflow) => {
    setIsSubmitting(true);
    try {
      await workflowApi.reopenWorkflow(item.id, carId!);
      dispatch(showSuccessNotification(i18n.t));
      setShowReopenDialog(false);
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
    setIsSubmitting(false);
  };

  const getIcon = (item: Workflow) => {
    if (!item.state || !item.createdAt) return undefined;

    const props = getIconProps(i18n.t, item.state, item.createdAt);

    return (
      <Inline title={props.title}>
        <Icon glyph={props.glyph} className={classNames(styles.icon)} color={props.color} />
        <span>- {props.title}</span>
      </Inline>
    );
  };

  const lastImageLog = dataWorkflow?.logs
    .filter((x) => x.action === "image-supplied")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .at(-1);

  return (
    <ModalDialog onClose={onClose} title="Workflow">
      {!dataWorkflow && <LoadingSpinner />}

      {!!dataWorkflow && (
        <table className={styles.table}>
          <tbody>
            <tr>
              <td width={1}>
                <b>{i18n.t.common.type}:</b>
              </td>
              <td>{dataWorkflow.carTask.taskName}</td>
            </tr>
            <tr>
              <td>
                <b>{i18n.t.common.state}:</b>
              </td>
              <td>{getIcon(dataWorkflow)}</td>
            </tr>
            <tr>
              <td>
                <b>{i18n.t.common.history}:</b>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <Table<WorkflowLog>
                  renderRow={(item) => {
                    const hasAttachments = !!dataAttachment && dataAttachment.total > 0;
                    const isCurrentOne = item.id == lastImageLog?.id;
                    return (
                      <>
                        <td>{formatTimeStampNumeric(item.createdAt, i18n.language)}</td>
                        <td>{item.author.fullName}</td>
                        <td>{item.action}</td>
                        <td>
                          {!!item.notes ? (
                            <div title={i18n.t.common.notes}>
                              <Icon
                                onClick={() => setShowTextDialog(item.notes)}
                                className={styles.icon}
                                glyph={SvgInfo}
                              />
                            </div>
                          ) : item.action == "image-supplied" ? (
                            <div
                              title={
                                !hasAttachments || !isCurrentOne
                                  ? i18n.t.common.imagesDeleted
                                  : i18n.t.common.viewImages
                              }
                            >
                              <Icon
                                onClick={hasAttachments && isCurrentOne ? () => setShowImagesDialog(true) : undefined}
                                className={classNames(
                                  styles.icon,
                                  !hasAttachments || !isCurrentOne ? styles.iconDisabled : undefined,
                                )}
                                glyph={SvgAttachFile}
                              />
                            </div>
                          ) : undefined}
                        </td>
                      </>
                    );
                  }}
                  items={dataWorkflow.logs}
                />
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {dataWorkflow?.state == "pending" && (
        <>
          <SubmitBar>
            <Button buttonProps={{ onClick: () => setShowCancelDialog(true) }} type="secondary">
              {i18n.t.common.cancelWorkflow}
            </Button>
            <Button buttonProps={{ onClick: () => setShowNotifyDialog(dataWorkflow) }}>
              {i18n.t.common.notifyDriver}
            </Button>
          </SubmitBar>
        </>
      )}

      {(dataWorkflow?.state == "cancelled" || dataWorkflow?.state == "closed") && (
        <div>
          <SubmitBar>
            <Button
              buttonProps={{
                onClick: () => setShowReopenDialog(true),
              }}
            >
              {i18n.t.common.reopen}
            </Button>
          </SubmitBar>
        </div>
      )}

      {dataWorkflow?.state == "image-supplied" && (
        <div>
          <SubmitBar>
            <Button buttonProps={{ onClick: () => setShowCancelDialog(true) }} type="secondary">
              {i18n.t.common.cancelWorkflow}
            </Button>
            <Button buttonProps={{ onClick: () => setShowImagesDialog(true) }}>{i18n.t.common.viewImages}</Button>
          </SubmitBar>
        </div>
      )}

      {showImagesDialog && dataAttachment?.items && (
        <ModalDialog onClose={() => setShowImagesDialog(false)} title={i18n.t.common.checkWorkflow}>
          <ListFiles files={dataAttachment?.items} />

          {dataWorkflow?.logs.at(-1)?.action == "image-supplied" && (
            <SubmitBar>
              <Button
                type="secondary"
                buttonProps={{
                  onClick: () => setShowImagesDialog(false),
                }}
              >
                {i18n.t.common.back}
              </Button>
              <Button
                type="remove"
                buttonProps={{
                  onClick: () => setShowRejectDialog(true),
                }}
              >
                {i18n.t.common.rejectImages}
              </Button>
              <Button
                buttonProps={{
                  onClick: () => setShowAcceptDialog(true),
                }}
              >
                {i18n.t.common.acceptImages}
              </Button>
            </SubmitBar>
          )}
        </ModalDialog>
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}

      {showAcceptDialog && (
        <ModalDialog
          title={i18n.t.common.acceptImages}
          onClose={() => setShowAcceptDialog(false)}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              onClick: () => setShowAcceptDialog(false),
              type: "secondary",
            },
            {
              title: i18n.t.common.send,
              onClick: () => approveImages(),
            },
          ]}
        >
          <div>{i18n.t.common.acceptImagesDialog}</div>
        </ModalDialog>
      )}

      {showRejectDialog && (
        <ModalDialog title={i18n.t.common.rejectImages} onClose={() => setShowRejectDialog(false)}>
          <RejectForm onSubmit={rejectImages} onClose={() => setShowRejectDialog(false)} isDisabled={isSubmitting} />
        </ModalDialog>
      )}

      {!!showNotifyDialog && (
        <ModalDialog
          onClose={() => setShowNotifyDialog(undefined)}
          title={i18n.t.common.notifyDriver}
          isSubmitting={isSubmitting}
          actions={[
            {
              type: "secondary",
              onClick: () => setShowNotifyDialog(undefined),
              title: i18n.t.common.cancel,
            },
            {
              title: i18n.t.common.send,
              onClick: () => notifyDriver(showNotifyDialog),
            },
          ]}
        >
          <div>{i18n.t.common.driverAlreadyNotified}</div>
        </ModalDialog>
      )}

      {!!showCancelDialog && (
        <ModalDialog
          onClose={() => setShowCancelDialog(false)}
          title={i18n.t.common.cancelWorkflow}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.t.common.back,
              onClick: () => setShowCancelDialog(false),
              type: "secondary",
            },
            {
              title: i18n.t.common.continue,
              onClick: () => dataWorkflow && cancelWorkflow(dataWorkflow),
            },
          ]}
        >
          <div>{i18n.t.common.cancelWorkflowRequest}</div>
        </ModalDialog>
      )}

      {!!showReopenDialog && (
        <ModalDialog
          onClose={() => setShowReopenDialog(false)}
          title={i18n.t.common.reopenWorkflow}
          actions={[
            {
              title: i18n.t.common.back,
              onClick: () => setShowReopenDialog(false),
              type: "secondary",
            },
            {
              title: i18n.t.common.reopen,
              onClick: () => dataWorkflow && reopenWorkflow(dataWorkflow),
            },
          ]}
          isSubmitting={isSubmitting}
        >
          <div>{i18n.t.common.emailSendToDriver}</div>
        </ModalDialog>
      )}

      {!!showTextDialog && (
        <ModalDialog title={i18n.t.common.notes} onClose={() => setShowTextDialog(undefined)}>
          <Textarea disabled style={{ width: "100%" }}>
            {showTextDialog}
          </Textarea>
        </ModalDialog>
      )}
    </ModalDialog>
  );
};

export default CheckWorkflowDialog;
